import { create } from 'zustand';
import { AssignmentHistory, CreateAssignmentRequest } from '../types';
import { 
  getAssignmentHistory,
  createAssignmentHistory,
  updateAssignmentHistory,
  deleteAssignmentHistory,
  getVehicleAssignments,
  createVehicleAssignment,
  updateVehicleAssignment,
  deleteVehicleAssignment,
  getVehicles,
  getDrivers,
  updateVehicle,
  updateDriver
} from '../api/supabase';

interface AssignmentState {
  assignments: AssignmentHistory[];
  vehicleAssignments: any[];
  loading: boolean;
  error: string | null;
  
  fetchAssignments: () => Promise<void>;
  createAssignment: (assignment: CreateAssignmentRequest) => Promise<AssignmentHistory>;
  updateAssignment: (id: string, updates: Partial<AssignmentHistory>) => Promise<void>;
  completeAssignment: (id: string, completedBy: string, returnDate?: string) => Promise<void>;
  cancelAssignment: (id: string) => Promise<void>;
  
  // Vehicle assignment functions
  fetchVehicleAssignments: () => Promise<void>;
  createVehicleAssignment: (assignment: any) => Promise<any>;
  updateVehicleAssignment: (id: string, updates: any) => Promise<void>;
  deleteVehicleAssignment: (id: string) => Promise<void>;
  
  // Enhanced unassignment with delivery form workflow
  completeVehicleUnassignment: (vehicleId: string, driverId: string, deliveryData: any) => Promise<void>;
  
  // Validation function
  validateAssignmentConstraints: (vehicleId: string, driverId: string, secondaryDriverId?: string | null, isSharedAssignment?: boolean) => Promise<void>;
}

export const useAssignmentStore = create<AssignmentState>((set, get) => ({
  assignments: [],
  vehicleAssignments: [],
  loading: false,
  error: null,

  fetchAssignments: async () => {
    set({ loading: true, error: null });
    try {
      console.log('🔄 AssignmentStore: Fetching assignments from API...');
      const assignments = await getAssignmentHistory();
      
      console.log(`📊 AssignmentStore: Retrieved ${assignments.length} assignments`);
      
      // Debug assignment data
      const sharedAssignments = assignments.filter(a => a.is_shared);
      const assignmentsWithSecondary = assignments.filter(a => a.secondary_driver_id);
      
      console.log(`🤝 AssignmentStore: Found ${sharedAssignments.length} shared assignments`);
      console.log(`👥 AssignmentStore: Found ${assignmentsWithSecondary.length} assignments with secondary drivers`);
      
      // Log recent assignments for debugging
      if (assignments.length > 0) {
        console.log('📋 AssignmentStore: Recent assignments:');
        assignments.slice(0, 3).forEach((assignment, index) => {
          console.log(`  ${index + 1}. ID: ${assignment.id}`);
          console.log(`     Vehicle: ${assignment.vehicle?.license_plate || 'Unknown'}`);
          console.log(`     Primary: ${assignment.primaryDriver?.name || 'Unknown'}`);
          console.log(`     Secondary: ${assignment.secondaryDriver?.name || 'None'}`);
          console.log(`     Secondary ID: ${assignment.secondary_driver_id || 'None'}`);
          console.log(`     Shared: ${assignment.is_shared ? 'Yes' : 'No'}`);
          console.log(`     Status: ${assignment.status}`);
          console.log('');
        });
      }
      
      set({ assignments, loading: false });
    } catch (error: any) {
      console.error('Error fetching assignments:', error);
      set({ loading: false, error: error.message });
    }
  },

  createAssignment: async (assignmentData) => {
    set({ loading: true, error: null });
    try {
      // VALIDATION: Check constraints before creating assignment
      await get().validateAssignmentConstraints(
        assignmentData.vehicle_id, 
        assignmentData.driver_id, 
        assignmentData.secondary_driver_id,
        assignmentData.is_shared
      );

      // Transform CreateAssignmentRequest to AssignmentHistory format
      const assignmentHistoryData = {
        vehicle_id: assignmentData.vehicle_id,
        primary_driver_id: assignmentData.driver_id,
        secondary_driver_id: assignmentData.secondary_driver_id,
        assignment_date: assignmentData.assignment_date,
        return_date: assignmentData.expected_return_date,
        daily_rate: assignmentData.daily_rate || 0,
        monthly_rent_rate: assignmentData.monthly_rate,
        is_shared: assignmentData.is_shared || false,
        assignment_type: assignmentData.assignment_type || 'rental',
        status: assignmentData.status || 'active'
      };

      const newAssignment = await createAssignmentHistory(assignmentHistoryData);

      // UPDATE STATUS: After successful assignment creation, update vehicle and driver statuses
      console.log('📝 Updating vehicle and driver statuses after assignment...');
      
      await Promise.all([
        // Update vehicle status to 'work'
        updateVehicle(assignmentData.vehicle_id, { 
          car_status: 'work',
          current_driver_id: assignmentData.driver_id
        }),
        // Update primary driver with assigned vehicle
        updateDriver(assignmentData.driver_id, { 
          current_vehicle_id: assignmentData.vehicle_id
        }),
        // Update secondary driver if provided
        ...(assignmentData.secondary_driver_id ? [
          updateDriver(assignmentData.secondary_driver_id, { 
            current_vehicle_id: assignmentData.vehicle_id 
          })
        ] : [])
      ]);

      console.log('✅ Assignment created and statuses updated successfully');

      // Refetch assignments to get complete data with joins
      console.log('🔄 Refetching assignments with complete data...');
      const updatedAssignments = await getAssignmentHistory();
      console.log('📊 Fetched assignments after creation:', updatedAssignments.length);
      
      set({ 
        assignments: updatedAssignments,
        loading: false 
      });

      console.log('💾 Assignment store updated with', updatedAssignments.length, 'assignments');

      // Refresh other stores to update cached data
      console.log('🔄 Refreshing driver and vehicle stores...');
      try {
        // Use a more reliable approach to refresh other stores
        // This will trigger a refresh if the stores are mounted
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refreshStores'));
        }
        console.log('✅ Store refresh event dispatched');
      } catch (refreshError) {
        console.warn('⚠️ Error dispatching refresh event:', refreshError);
      }

      return newAssignment;
    } catch (error: any) {
      console.error('Error creating assignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  updateAssignment: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const updatedAssignment = await updateAssignmentHistory(id, updates);

      // Update local state
      set({
        assignments: get().assignments.map(assignment =>
          assignment.id === id ? updatedAssignment : assignment
        ),
        loading: false
      });
    } catch (error: any) {
      console.error('Error updating assignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  completeAssignment: async (id, completedBy, returnDate = new Date().toISOString()) => {
    set({ loading: true, error: null });
    try {
      // First, get the assignment details to find drivers and vehicle
      const assignment = get().assignments.find(a => a.id === id);
      if (!assignment) {
        throw new Error('Assignment not found');
      }

      console.log('🏁 Completing assignment:', {
        id,
        vehicle: assignment.vehicle_id,
        primaryDriver: assignment.primary_driver_id,
        secondaryDriver: assignment.secondary_driver_id,
        isShared: assignment.is_shared
      });

      // Update assignment status
      await get().updateAssignment(id, {
        status: 'completed',
        return_date: returnDate,
        updated_at: new Date().toISOString()
      });

      // Clear vehicle and driver assignments
      console.log('🧹 Clearing vehicle and driver assignments...');
      
      const clearOperations = [
        // Clear vehicle assignment
        updateVehicle(assignment.vehicle_id, {
          car_status: 'available',
          current_driver_id: undefined
        }),
        // Clear primary driver assignment
        updateDriver(assignment.primary_driver_id, {
          current_vehicle_id: undefined
        })
      ];

      // Clear secondary driver assignment if exists
      if (assignment.secondary_driver_id) {
        console.log('🤝 Clearing secondary driver assignment...');
        clearOperations.push(
          updateDriver(assignment.secondary_driver_id, {
            current_vehicle_id: undefined
          })
        );
      }

      await Promise.all(clearOperations);
      
      console.log('✅ Assignment completed and all records cleared');
      
      // Refresh stores after completion
      try {
        await get().fetchAssignments();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refreshStores'));
        }
        console.log('✅ Stores refreshed after completion');
      } catch (refreshError) {
        console.warn('⚠️ Error refreshing stores after completion:', refreshError);
      }
      
      set({ loading: false });
    } catch (error: any) {
      console.error('Error completing assignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  cancelAssignment: async (id) => {
    set({ loading: true, error: null });
    try {
      // First, get the assignment details to find drivers and vehicle
      const assignment = get().assignments.find(a => a.id === id);
      if (!assignment) {
        throw new Error('Assignment not found');
      }

      console.log('🚫 Cancelling assignment:', {
        id,
        vehicle: assignment.vehicle_id,
        primaryDriver: assignment.primary_driver_id,
        secondaryDriver: assignment.secondary_driver_id,
        isShared: assignment.is_shared
      });

      // Update assignment status
      await get().updateAssignment(id, {
        status: 'cancelled',
        return_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

      // Clear vehicle and driver assignments
      console.log('🧹 Clearing vehicle and driver assignments...');
      
      const clearOperations = [
        // Clear vehicle assignment
        updateVehicle(assignment.vehicle_id, {
          car_status: 'available',
          current_driver_id: undefined
        }),
        // Clear primary driver assignment
        updateDriver(assignment.primary_driver_id, {
          current_vehicle_id: undefined
        })
      ];

      // Clear secondary driver assignment if exists
      if (assignment.secondary_driver_id) {
        console.log('🤝 Clearing secondary driver assignment...');
        clearOperations.push(
          updateDriver(assignment.secondary_driver_id, {
            current_vehicle_id: undefined
          })
        );
      }

      await Promise.all(clearOperations);
      
      console.log('✅ Assignment cancelled and all records cleared');
      
      // Refresh stores after cancellation
      try {
        await get().fetchAssignments();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('refreshStores'));
        }
        console.log('✅ Stores refreshed after cancellation');
      } catch (refreshError) {
        console.warn('⚠️ Error refreshing stores after cancellation:', refreshError);
      }
      
      set({ loading: false });
    } catch (error: any) {
      console.error('Error cancelling assignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  // Vehicle assignment functions
  fetchVehicleAssignments: async () => {
    set({ loading: true, error: null });
    try {
      const vehicleAssignments = await getVehicleAssignments();
      set({ vehicleAssignments, loading: false });
    } catch (error: any) {
      console.error('Error fetching vehicle assignments:', error);
      set({ loading: false, error: error.message });
    }
  },

  createVehicleAssignment: async (assignmentData) => {
    set({ loading: true, error: null });
    try {
      const newAssignment = await createVehicleAssignment(assignmentData);

      set({ 
        vehicleAssignments: [newAssignment, ...get().vehicleAssignments],
        loading: false 
      });

      return newAssignment;
    } catch (error: any) {
      console.error('Error creating vehicle assignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  updateVehicleAssignment: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const updatedAssignment = await updateVehicleAssignment(id, updates);

      // Update local state
      set({
        vehicleAssignments: get().vehicleAssignments.map(assignment =>
          assignment.id === id ? updatedAssignment : assignment
        ),
        loading: false
      });
    } catch (error: any) {
      console.error('Error updating vehicle assignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  deleteVehicleAssignment: async (id) => {
    set({ loading: true, error: null });
    try {
      await deleteVehicleAssignment(id);

      // Update local state - remove the deleted assignment
      set({
        vehicleAssignments: get().vehicleAssignments.filter(assignment => assignment.id !== id),
        loading: false
      });
    } catch (error: any) {
      console.error('Error deleting vehicle assignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  // Enhanced unassignment with delivery form workflow
  completeVehicleUnassignment: async (vehicleId, driverId, deliveryData) => {
    set({ loading: true, error: null });
    try {
      console.log('🔄 Starting complete vehicle unassignment process...');
      
      // 1. Find active assignment records
      const activeAssignments = get().assignments.filter(a => 
        a.vehicle_id === vehicleId && 
        a.primary_driver_id === driverId && 
        a.status === 'active'
      );
      
      const activeVehicleAssignments = get().vehicleAssignments.filter(a => 
        a.vehicleId === vehicleId && 
        a.driverId === driverId && 
        a.assignmentStatus === 'active'
      );
      
      // 2. Complete assignment history records
      for (const assignment of activeAssignments) {
        await get().updateAssignment(assignment.id, {
          status: 'completed',
          return_date: deliveryData.deliveredAt || new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
      
      // 3. Complete vehicle assignment records
      for (const assignment of activeVehicleAssignments) {
        await get().updateVehicleAssignment(assignment.id, {
          assignment_status: 'completed',
          end_date: deliveryData.deliveredAt || new Date().toISOString()
        });
      }
      
      // 4. Create delivery record
      const { createVehicleDelivery } = await import('../api/supabase');
      const deliveryRecord = await createVehicleDelivery({
        assignmentId: activeAssignments[0]?.id || null,
        driverId,
        vehicleId,
        deliveryDate: new Date(deliveryData.deliveredAt || new Date()).toISOString().split('T')[0],
        deliveryTime: new Date(deliveryData.deliveredAt || new Date()).toTimeString().split(' ')[0],
        finalMileage: deliveryData.mileageOut,
        fuelLevel: deliveryData.fuelLevel,
        exteriorCondition: deliveryData.exteriorCondition,
        interiorCondition: deliveryData.interiorCondition,
        hasAllKeys: deliveryData.keyStatus === 'returned',
        hasAllDocuments: deliveryData.documentsReturned?.length > 0 || true,
        deliveredBy: deliveryData.deliveredBy,
        receivedBy: 'Fleet Manager',
        additionalNotes: deliveryData.notes || `Vehicle returned. Final mileage: ${deliveryData.mileageOut}, Fuel: ${deliveryData.fuelLevel}%`,
      });
      
      console.log('✅ Vehicle delivery record created:', deliveryRecord);
      set({ loading: false });
      
      return deliveryRecord;
    } catch (error: any) {
      console.error('Error completing vehicle unassignment:', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  // Validation function to prevent duplicate assignments
  validateAssignmentConstraints: async (vehicleId: string, driverId: string, secondaryDriverId?: string | null, isSharedAssignment?: boolean) => {
    console.log('🔍 Validating assignment constraints...', { vehicleId, driverId, secondaryDriverId });
    
    // Get fresh data from database
    const [vehicles, drivers] = await Promise.all([
      getVehicles(),
      getDrivers()
    ]);

    // Find the vehicle and drivers
    const vehicle = vehicles.find(v => v.id === vehicleId);
    const driver = drivers.find(d => d.id === driverId);
    const secondaryDriver = secondaryDriverId ? drivers.find(d => d.id === secondaryDriverId) : null;

    if (!vehicle) {
      throw new Error('Vehicle not found');
    }
    if (!driver) {
      throw new Error('Primary driver not found');
    }
    if (secondaryDriverId && !secondaryDriver) {
      throw new Error('Secondary driver not found');
    }

    // Check if vehicle is already assigned (more lenient for shared assignments)
    if (vehicle.carStatus === 'work' && !isSharedAssignment) {
      throw new Error(`Vehicle ${vehicle.licensePlate} is already assigned to another driver`);
    } else if (vehicle.carStatus === 'work' && isSharedAssignment) {
      console.log(`⚠️ Vehicle ${vehicle.licensePlate} is already assigned but allowing for shared assignment`);
    }

    // Check if primary driver already has a vehicle (more lenient for shared assignments)
    if (driver.currentVehicleId && !isSharedAssignment) {
      throw new Error(`Driver ${driver.name} already has a vehicle assigned (${driver.currentVehicleId})`);
    } else if (driver.currentVehicleId && isSharedAssignment) {
      console.log(`⚠️ Driver ${driver.name} already has a vehicle but allowing for shared assignment`);
    }

    // Check if secondary driver already has a vehicle (more lenient for shared assignments)
    if (secondaryDriver?.currentVehicleId && !isSharedAssignment) {
      throw new Error(`Secondary driver ${secondaryDriver.name} already has a vehicle assigned (${secondaryDriver.currentVehicleId})`);
    } else if (secondaryDriver?.currentVehicleId && isSharedAssignment) {
      console.log(`⚠️ Secondary driver ${secondaryDriver.name} already has a vehicle but allowing for shared assignment`);
    }

    console.log('✅ Assignment validation passed');
  },

}));
